import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { surveyApiService } from '../../api/surveyApi';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
    PieChart,
    Pie
} from 'recharts';
import {
    ChevronRightIcon,
    ArrowPathIcon,
    ArrowDownTrayIcon,
    ChartPieIcon,
    PresentationChartBarIcon,
    UsersIcon,
    CheckBadgeIcon,
    ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';

const SurveyAnalytics = () => {
    const { slug } = useParams();
    const [survey, setSurvey] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [surveyData, analyticsData] = await Promise.all([
                    surveyApiService.getSurveyDetail(slug),
                    surveyApiService.getSurveyAnalytics(slug)
                ]);

                if (!surveyData) throw new Error('Anket bilgileri alınamadı.');
                if (!analyticsData) throw new Error('Analiz verileri alınamadı.');

                setSurvey(surveyData);
                setAnalytics(analyticsData);
            } catch (err) {
                console.error('Error fetching analytics:', err);
                setError(err.message || 'Bir hata oluştu.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [slug]);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[600px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-8 text-center space-y-4">
                <div className="text-rose-500 font-bold text-xl">{error}</div>
                <button
                    onClick={() => window.location.reload()}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-xl font-bold text-sm"
                >
                    Tekrar Dene
                </button>
            </div>
        );
    }

    if (!analytics || !survey) return <div className="p-8 text-center text-rose-500 font-bold">Veri bulunamadı.</div>;

    const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];

    const renderQuestionChart = (question) => {
        if (question.breakdown) {
            // Merge counts for duplicate labels to keep chart clean
            const mergedMap = new Map();
            question.breakdown.forEach(item => {
                const name = item.label.tr || item.label;
                const currentCount = mergedMap.get(name) || 0;
                mergedMap.set(name, currentCount + item.count);
            });

            const data = Array.from(mergedMap.entries()).map(([name, count]) => ({
                name,
                count
            }));

            return (
                <div className="h-64 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} layout="vertical" margin={{ left: 20, right: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#E2E8F0" />
                            <XAxis type="number" hide />
                            <YAxis
                                dataKey="name"
                                type="category"
                                width={120}
                                tick={{ fontSize: 10, fontWeight: 700, fill: '#64748B' }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <Tooltip
                                cursor={{ fill: 'transparent' }}
                                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                            />
                            <Bar dataKey="count" radius={[0, 8, 8, 0]} barSize={24}>
                                {data.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        if (question.average !== undefined) {
            return (
                <div className="flex flex-col items-center justify-center h-64 space-y-4">
                    <div className="relative flex items-center justify-center">
                        <svg className="w-40 h-40">
                            <circle
                                className="text-slate-100 dark:text-slate-800"
                                strokeWidth="8"
                                stroke="currentColor"
                                fill="transparent"
                                r="70"
                                cx="80"
                                cy="80"
                            />
                            <circle
                                style={{ color: 'var(--primary-color, #6366f1)' }}
                                strokeWidth="8"
                                strokeDasharray={440}
                                strokeDashoffset={440 - (440 * (question.average / (question.type === 'range_100' ? 100 : 10)))}
                                strokeLinecap="round"
                                stroke="currentColor"
                                fill="transparent"
                                r="70"
                                cx="80"
                                cy="80"
                            />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-4xl font-black text-slate-900 dark:text-white">{question.average}</span>
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">ORTALAMA</span>
                        </div>
                    </div>
                </div>
            );
        }

        if (question.samples && question.samples.length > 0) {
            return (
                <div className="space-y-3 h-64 overflow-y-auto pr-2 custom-scrollbar">
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Son Yanıtlar</div>
                    {question.samples.map((sample, idx) => (
                        <div key={idx} className="p-3 bg-slate-50 dark:bg-slate-800/40 rounded-xl border border-slate-100 dark:border-slate-800 text-xs font-medium text-slate-600 dark:text-slate-400 italic">
                            "{sample}"
                        </div>
                    ))}
                </div>
            );
        }

        return <div className="text-center py-10 text-slate-400 italic font-medium">Bu soru tipi için grafik gösterilemiyor.</div>;
    };

    return (
        <div className="px-3 py-4 sm:px-4 md:p-8 max-w-7xl mx-auto space-y-6 md:space-y-8 pb-16 md:pb-20">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="space-y-1">
                    <div className="flex items-center gap-2 text-slate-500 text-sm mb-2 font-bold">
                        <Link to="/survey/list" className="hover:text-indigo-600 transition-colors">Anketler</Link>
                        <ChevronRightIcon className="w-4 h-4" />
                        <span>Analiz ve Raporlar</span>
                    </div>
                    <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                        {survey.title?.tr || Object.values(survey.title || {})[0]}
                    </h1>
                    <p className="text-slate-500 font-medium">Anket verilerinin detaylı istatistiksel raporu.</p>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={() => window.location.reload()}
                        className="p-2.5 text-slate-500 hover:text-indigo-600 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl transition-all"
                    >
                        <ArrowPathIcon className="w-5 h-5" />
                    </button>
                    <button
                        className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-xl font-bold text-sm hover:bg-indigo-700 transition-all shadow-lg active:scale-95"
                    >
                        <ArrowDownTrayIcon className="w-4 h-4" />
                        PDF Raporu İndir
                    </button>
                </div>
            </div>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {[
                    { label: 'Toplam Katılım', value: analytics.total_submissions, icon: UsersIcon, color: 'text-indigo-600', bg: 'bg-indigo-50 dark:bg-indigo-500/10' },
                    { label: 'Tamamlanan', value: analytics.completed_submissions, icon: CheckBadgeIcon, color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-500/10' },
                    { label: 'Tamamlama Oranı', value: `%${analytics.completion_rate}`, icon: ArrowTrendingUpIcon, color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-500/10' },
                    { label: 'Bölüm Sayısı', value: survey.sections.length, icon: PresentationChartBarIcon, color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-500/10' }
                ].map((stat, i) => (
                    <div key={i} className="bg-white dark:bg-slate-900 p-6 rounded-[2rem] border border-slate-100 dark:border-slate-800 shadow-sm flex items-center gap-4">
                        <div className={`p-4 ${stat.bg} ${stat.color} rounded-2xl`}>
                            <stat.icon className="w-6 h-6" />
                        </div>
                        <div>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{stat.label}</p>
                            <p className="text-2xl font-black text-slate-900 dark:text-white uppercase leading-tight">{stat.value}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Questions Breakdown Section */}
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-3">
                        <ChartPieIcon className="w-6 h-6 text-indigo-500" />
                        Soru Bazlı Analiz
                    </h2>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8">
                    {analytics.questions.map((q, idx) => (
                        <div key={q.id} className="bg-white dark:bg-slate-900 p-4 md:p-8 rounded-3xl md:rounded-[3rem] border border-slate-100 dark:border-slate-800 shadow-sm space-y-4 md:space-y-6">
                            <div className="space-y-2">
                                <div className="flex items-center gap-3">
                                    <span className="w-6 h-6 flex items-center justify-center bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 text-[10px] font-black rounded-lg">
                                        {idx + 1}
                                    </span>
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                        {q.type}
                                    </span>
                                </div>
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white leading-snug">
                                    {q.title?.tr || Object.values(q.title || {})[0]}
                                </h3>
                            </div>

                            <div className="pt-4">
                                {renderQuestionChart(q)}
                            </div>

                            <div className="pt-6 border-t border-slate-50 dark:border-slate-800/50 flex items-center justify-between">
                                <div className="text-xs font-bold text-slate-500">
                                    Toplam <span className="text-slate-900 dark:text-white">{q.total_responses}</span> geçerli yanıt
                                </div>
                                <Link
                                    to={`/survey/submissions/${slug}`}
                                    className="text-[10px] font-black text-indigo-500 hover:text-indigo-600 uppercase tracking-widest"
                                >
                                    Yanıtları İncele
                                </Link>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SurveyAnalytics;
